import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ReceivedPaymentForm = () => {
  const [formData, setFormData] = useState({
    sales_contract_id: '',
    received_date: '',
    amount_received: '',
    payment_type: 'ADVANCE',
    remarks: '',
    status: 'In Process'
  });

  const [loading, setLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const [salesContracts, setSalesContracts] = useState([]);
  const navigate = useNavigate();

  const FIELD_LIMITS = {
    sales_contract_id: 10, // int, UI limit
    received_date: 10, // yyyy-mm-dd
    amount_received: 14, // decimal(14,2), input type number
    payment_type: 20, // enum
    remarks: 1000, // text, UI limit
    status: 101,
    approved_by: 100,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields based on database schema
    const requiredFields = [
      'sales_contract_id', 'received_date', 'amount_received', 'payment_type'
    ];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  // Fetch sales contracts for dropdown
  useEffect(() => {
    const fetchSalesContracts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-sales-contracts`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (res.ok && Array.isArray(data.data)) {
          setSalesContracts(data.data);
        }
      } catch (err) {
        console.error('Error fetching sales contracts:', err);
      }
    };

    fetchSalesContracts();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    // Validation: check required fields
    const requiredFields = [
      'sales_contract_id', 'received_date', 'amount_received', 'payment_type'
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '' || formData[field] === 0) {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/masters/received-payment-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success('Received Payment Created Successfully!');
        setFormData({
          sales_contract_id: '',
          received_date: '',
          amount_received: '',
          payment_type: 'ADVANCE',
          remarks: '',
          status: 'In Process'
        });
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Received Payment Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium mb-1">Sales Contract</label>
              <SearchableDropdown
                options={salesContracts}
                value={formData.sales_contract_id}
                onChange={(value) => handleDropdownChange('sales_contract_id', value)}
                placeholder="Select Sales Contract"
                displayKey="sales_contract_no"
                valueKey="sales_contract_id"
                required
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Received Date</label>
              <input
                type="date"
                name="received_date"
                value={formData.received_date}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Amount Received</label>
              <input
                type="number"
                step="0.01"
                name="amount_received"
                value={formData.amount_received}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                placeholder="Enter amount"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Payment Type</label>
              <select
                name="payment_type"
                value={formData.payment_type}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              >
                <option value="ADVANCE">ADVANCE</option>
                <option value="General">General</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-[12px] font-medium mb-1">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.remarks}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none resize-none"
                rows={3}
                placeholder="Enter any additional remarks"
              />
            </div>
          </div>
        </div>

        <div className="px-3 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
            style={{ minWidth: '100px' }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Payment'}
          </button>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
      </form>
    </div>
  );
};

export default ReceivedPaymentForm;
